import { Component, Input, OnDestroy } from '@angular/core';
import { RequestComponent } from '../../request/request.component';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { Subscription } from 'rxjs';
import { inject } from '@angular/core';
import {LeadCollectorPrefillService} from "../../../services/lead-collector-prefill.service";


@Component({
  selector: 'app-floating-contact-form',
  templateUrl: './floating-contact-form.component.html',
  styleUrls: ['./floating-contact-form.component.scss'],
  standalone: false
})
export class FloatingContactFormComponent extends RequestComponent implements OnDestroy {
  @Input() isFixed: boolean = true;
  searchParams: { country?: string; city?: string } = {};
  override scope?: string = 'request-popup';
  @Input() smallScreen: boolean = false;

  private prefillService: LeadCollectorPrefillService = inject(LeadCollectorPrefillService);

  lastOpenModal?: any;
  lastOpenModalInstance?: any;
  modalSub?: Subscription;
  private langChangeSub?: Subscription;


  override async ngOnInit(): Promise<void> {
    this.viewStatus = this.ViewStatus.LOADING;

    this.initRequestInfoForm();
    this.getSearchParams();

    this.langChangeSub = this.translateService.onLangChange.subscribe(() => {
      this.initRequestInfoForm();
      if (!this.smallScreen) {
        this.requestInfoFields = this.removeAttachmentFields(this.requestInfoFields);
      }
    });

    this.modalSub = this.modalService.activeInstances.subscribe(async (modals) => {
      if (this.lastOpenModal && !modals.includes(this.lastOpenModal)) {
        if (this.lastOpenModalInstance && this.lastOpenModalInstance.clearData) {
          this.lastOpenModalInstance.clearData();
        }
        this.formModel = {};
        this.lastOpenModal = undefined;
        this.lastOpenModalInstance = undefined;
      }

      if (modals.length) {
        this.lastOpenModal = modals[modals.length - 1];
        this.lastOpenModalInstance = this.lastOpenModal.componentInstance;
      }
    });

    if (!this.smallScreen) {
      this.requestInfoFields = this.removeAttachmentFields(this.requestInfoFields);
    }

    try {

      this.viewStatus = this.ViewStatus.READY;
    } catch (error) {
      console.error('Error loading location data:', error);
      this.viewStatus = this.ViewStatus.READY;
    }

    this.checkAndOpenModalFromRoute();
  }

  removeAttachmentFields(fields: FormlyFieldConfig[]): FormlyFieldConfig[] {
    return fields
      .map((field) => {
        if (field.fieldGroup) {
          return {
            ...field,
            fieldGroup: this.removeAttachmentFields(field.fieldGroup),
          };
        }
        if (field.key === 'panoramicAttachments' || field.key === 'othersAttachments') {
          return null;
        }
        return field;
      })
      .filter((field): field is FormlyFieldConfig => !!field);
  }


  openOfferRequestModal() {
    const lcPrefill = this.prefillService.get();

    const componentConfig = {
      scope: 'quote',
      district: this.searchService.searchResult?.district,
      region: this.searchService.searchResult?.region,
      country: this.searchService.searchResult?.country,
      formModel: {
        requestDetails: this.formModel.requestDetails,
        dentalArch: this.formModel.dentalArch,
        treatmentType: this.formModel.treatmentType,
        dentalNeed: this.formModel.dentalNeed,
        requestMotivation: this.formModel.requestMotivation,
        // Prefill contact fields from LeadCollector if available
        ...(lcPrefill ? {
          firstname: lcPrefill.firstname,
          lastname: lcPrefill.lastname,
          phoneNumber: lcPrefill.phoneNumber,
          email: lcPrefill.email,
          city: lcPrefill.city
        } : {})
      },
      // Pass the LC access token so RequestComponent uses the complete endpoint
      lcAccessToken: lcPrefill?.token ?? null,
    };

    const options = {
      backdrop: 'static',
      size: 'lg',
      scrollable: true,
      container: 'body',
      panelClass: 'app-offcanvas-taller',
      position: 'bottom',
    };

    this.modalOffcanvasService.openComponent(RequestComponent, {
      scope: componentConfig.scope,
      district: componentConfig.district,
      region: componentConfig.region,
      country: componentConfig.country,
      formModel: componentConfig.formModel,
      isOffcanvas: this.smallScreen,
      lcAccessToken: componentConfig.lcAccessToken
    }, options)
      .then((result) => {
        this.requestInfoForm.reset();
        this.requestInfoForm.setErrors(null);
        if (lcPrefill) {
          this.prefillService.clear();
        }
      })
      .catch((err) => {
        console.log('Modal dismissed:', err);
      });

  }


  getSearchParams() {
    this.activatedRoute.queryParamMap.subscribe((queryParamMap) => {
      const queryCountry = queryParamMap.get('country');
      const cityRegion = queryParamMap.get('city');
      if (queryCountry || cityRegion) {
        this.searchParams = {
          country: queryCountry || this.searchParams.country,
          city: cityRegion || this.searchParams.city,
        };
      }
    });
  }

  checkAndOpenModalFromRoute() {
    this.activatedRoute.queryParamMap.subscribe((queryParamMap) => {
      const openQuote = queryParamMap.get('openQuote');

      if (openQuote === 'true') {
        setTimeout(() => {
          this.openOfferRequestModal();
        }, 300);

        this.router.navigate([], {
          queryParams: { openQuote: null },
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
      }
    });
  }

  getLocationDisplay(): string {
    if (this.viewStatus === this.ViewStatus.LOADING) {
      return '';
    }

    const translate = (value?: string): string => {
      if (!value || !value.trim()) return '';
      const translation = this.translateService.instant(value);
      return translation !== value ? translation : value;
    };

    let locationParts: string[] = [];

    if (this.searchService?.searchResult) {
      locationParts = [
        translate(this.searchService.searchResult.district?.name),
        translate(this.searchService.searchResult.region?.name),
        translate(this.searchService.searchResult.country?.name),
      ].filter(Boolean);

      if (locationParts.length > 0) {
        return locationParts.join(', ');
      }
    }

    if (this.searchParams?.city || this.searchParams?.country) {
      locationParts = [
        translate(this.searchParams.city),
        translate(this.searchParams.country),
      ].filter(part => part && part !== '1');

      if (locationParts.length > 0) {
        return locationParts.join(', ');
      }
    }

    return '';
  }


  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
    if (this.modalSub) {
      this.modalSub.unsubscribe();
    }
  }
}
